'use memo';

import { Outlet, createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { Text } from '@/components/Typography/Text';
import { useGanttDashboard } from '@/features/operation-event/useGanttDashboard';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { eventTitleSelectors } from '../-lib/eventTitleSelectors';
import { AttendanceEventDetailHeader } from './-components/AttendanceEventDetailHeader';
import { AttendanceEventDetailProvider } from './-context/attendance-event-detail-context';
import { buildAttendanceEventDetail, countAttendanceEventTasks } from './-mock/attendanceEventDetail';
export const Route = createFileRoute('/_staff/attendance-core/_layout/operation-events/$eventId/_layout')({
  component: AttendanceCoreOperationEventDetailLayout
});

/** 給与計算イベント詳細の共通レイアウト。ヘッダー（タブ含む）を持ち、本体は子ルートが描画する。 */
function AttendanceCoreOperationEventDetailLayout() {
  const {
    eventId
  } = Route.useParams();
  const {
    t
  } = useTranslation(defaultNS);
  const {
    data,
    isPending,
    isError
  } = useGanttDashboard();

  /** モックのため完了操作はこの画面内だけで保持する（実 API 実装時は mutation に置き換える） */
  const [completedTaskIds, setCompletedTaskIds] = useState<ReadonlySet<string>>(() => new Set());
  const detail = useMemo(() => {
    const base = buildAttendanceEventDetail(eventId);
    if (completedTaskIds.size === 0) {
      return base;
    }
    return {
      ...base,
      steps: base.steps.map(step => ({
        ...step,
        tasks: step.tasks.map(task => completedTaskIds.has(task.id) ? {
          ...task,
          status: 'done' as const
        } : task)
      }))
    };
  }, [completedTaskIds, eventId]);
  const taskCount = useMemo(() => countAttendanceEventTasks(detail), [detail]);
  const completeTask = useCallback((taskId: string) => {
    setCompletedTaskIds(prev => new Set(prev).add(taskId));
  }, []);
  const detailValue = useMemo(() => ({
    completeTask,
    detail
  }), [completeTask, detail]);
  const eventTitleKey = useMemo(() => {
    const eventRow = data?.rows.find(row => row.left.kind === 'event' && row.left.eventId === eventId);
    return eventRow?.left.kind === 'event' ? eventRow.left.titleKey : undefined;
  }, [data, eventId]);
  if (isPending) {
    return <ViewportLoader className="h-full w-full items-center" />;
  }
  if (isError || !data) {
    return <div className="flex flex-1 items-center justify-center p-12">
        <Text variant="label" className="text-error-on-neutral-fill">
          {t($ => $.pages.attendanceCore.dashboard.gantt.loadError)}
        </Text>
      </div>;
  }
  const eventTitleSelector = eventTitleKey ? eventTitleSelectors[eventTitleKey] : undefined;
  return <AttendanceEventDetailProvider value={detailValue}>
      <VStack className="h-full min-h-0 flex-1">
        <AttendanceEventDetailHeader eventId={eventId} eventTitle={eventTitleSelector ? t(eventTitleSelector) : eventId} completedTaskCount={taskCount.completed} totalTaskCount={taskCount.total} targetEmployeeCount={detail.targetEmployeeCount} closingDateIso={detail.closingDateIso} todayIso={detail.todayIso} />
        <Outlet />
      </VStack>
    </AttendanceEventDetailProvider>;
}